import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";

// ─── Keyframes ────────────────────────────────────────────────────────────────

const burst = keyframes`
  0%   { transform: scale(0);   opacity: 1; }
  55%  { transform: scale(18);  opacity: 1; }
  100% { transform: scale(30);  opacity: 0; }
`;

const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const overlayFadeOut = keyframes`
  0%   { opacity: 1; }
  70%  { opacity: 1; }
  100% { opacity: 0; }
`;

const iconPop = keyframes`
  0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
  40%  { transform: scale(1.3) rotate(10deg); opacity: 1; }
  70%  { transform: scale(0.9) rotate(-5deg); opacity: 1; }
  100% { transform: scale(1)  rotate(0deg);  opacity: 1; }
`;

const float1 = keyframes`
  0%   { transform: translateY(0px)   rotate(0deg);   opacity: 0.7; }
  100% { transform: translateY(-90vh) rotate(180deg); opacity: 0; }
`;
const float2 = keyframes`
  0%   { transform: translateY(0px)    rotate(0deg);   opacity: 0.5; }
  100% { transform: translateY(-80vh)  rotate(-360deg); opacity: 0; }
`;
const float3 = keyframes`
  0%   { transform: translateY(0px)   rotate(0deg);   opacity: 0.6; }
  100% { transform: translateY(-70vh) rotate(270deg); opacity: 0; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const PlayTransition = ({ onDone }) => {
  useEffect(() => {
    const timer = setTimeout(onDone, 780);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "linear-gradient(135deg, #1a0533 0%, #2d1b69 50%, #1a0533 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        animation: `${overlayFadeIn} 0.08s ease-out forwards`,
      }}
    >
      {/* Floating particles */}
      {[
        { left: "15%",  bottom: "10%", size: 18, color: "#FF6B9D", delay: "0s",    dur: "0.9s",  anim: float1 },
        { left: "30%",  bottom: "5%",  size: 14, color: "#FFD700", delay: "0.05s", dur: "1.05s", anim: float2 },
        { left: "50%",  bottom: "8%",  size: 22, color: "#4FC3F7", delay: "0.1s",  dur: "0.85s", anim: float3 },
        { left: "65%",  bottom: "3%",  size: 12, color: "#A5D6A7", delay: "0.02s", dur: "1.0s",  anim: float1 },
        { left: "80%",  bottom: "12%", size: 20, color: "#FF8A65", delay: "0.07s", dur: "0.95s", anim: float2 },
        { left: "8%",   bottom: "20%", size: 16, color: "#CE93D8", delay: "0.13s", dur: "0.88s", anim: float3 },
        { left: "90%",  bottom: "18%", size: 10, color: "#F48FB1", delay: "0.04s", dur: "1.1s",  anim: float1 },
        { left: "45%",  bottom: "2%",  size: 24, color: "#80DEEA", delay: "0.09s", dur: "0.92s", anim: float2 },
      ].map((p, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: p.left,
            bottom: p.bottom,
            width: p.size,
            height: p.size,
            borderRadius: "50%",
            bgcolor: p.color,
            animation: `${p.anim} ${p.dur} ${p.delay} ease-out forwards`,
          }}
        />
      ))}

      {/* The burst circle */}
      <Box
        sx={{
          position: "absolute",
          width: 120,
          height: 120,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, #FFD000 0%, #FF9A00 45%, #FF6B00 80%, #E040FB 100%)",
          animation: `${burst} 0.78s cubic-bezier(0.22, 1, 0.36, 1) forwards`,
          transformOrigin: "center center",
        }}
      />

      {/* Center icon */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          animation: `${iconPop} 0.5s 0.05s ease-out both`,
        }}
      >
        <SportsEsportsIcon sx={{ fontSize: "4rem", color: "#fff" }} />
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: "1.6rem",
            color: "#fff",
            letterSpacing: "6px",
            textTransform: "uppercase",
          }}
        >
          PLAY
        </Typography>
      </Box>

      {/* Fade-out veil */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(135deg, #1a0533 0%, #2d1b69 50%, #1a0533 100%)",
          animation: `${overlayFadeOut} 0.78s ease-in-out forwards`,
          pointerEvents: "none",
        }}
      />
    </Box>
  );
};

export default PlayTransition;
